import {IconButton, MultiChoice, Tooltip, useAnimatedPopper} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNotNil, values} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useRentalCalendarFilterOptions} from '../hooks/useRentalCalendarFilterOptions';
import {
  EMPTY_RENTAL_CALENDAR_FILTERS,
  type RentalCalendarFilterValues,
} from '../types/RentalCalendarFilterValues';
import {getRentalCalendarFilterLabel} from '../utils/getRentalCalendarFilterLabel';
import {setRentalCalendarFilter} from '../utils/setRentalCalendarFilter';

interface RentalCalendarFiltersProps extends RequiredTestIdProps {
  filters: RentalCalendarFilterValues;
  onFiltersChange: (filters: RentalCalendarFilterValues) => void;
}

export function RentalCalendarFilters(props: RentalCalendarFiltersProps) {
  const {options: filterOptions} = useRentalCalendarFilterOptions(props.filters);

  const {isOpen, togglePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: 'bottom-start',
    gutter: 4,
    // No `strategy: 'fixed'`: the trigger lives in the header with no clipping ancestor, and a
    // fixed panel stays at the viewport spot the trigger leaves behind once the page scrolls

    // Every select renders its menu in a body portal, so picking an option looks like an outside click
    shouldIgnoreOutsideClick: (target) => isNotNil(target.closest(CHOICE_MENU_SELECTOR)),
  });

  const activeCount = countActiveFilters(props.filters);

  const setFilter = (key: keyof RentalCalendarFilterValues) => (value: string[] | null) =>
    props.onFiltersChange(setRentalCalendarFilter(props.filters, key, value ?? []));

  return (
    <div ref={referenceRef} className="relative flex items-center">
      {/* The tooltip would otherwise sit on top of the panel it just opened */}
      <Tooltip label={i18n.t('page.datagrid.labels.filters')} isDisabled={isOpen}>
        <IconButton
          icon="content/filter_list"
          onClick={togglePopper}
          aria-label={i18n.t('page.datagrid.labels.filters')}
          data-testid={suffixTestId('filters-toggle', props)}
        />
      </Tooltip>
      {/* The button no longer carries a label, so the count is what signals an active filter */}
      <Show when={activeCount > 0}>
        <span
          className="pointer-events-none absolute -right-1 -top-1 rounded-full bg-severity-informational px-1 text-xs font-bold text-white"
          data-testid={suffixTestId('filters-activeCount', props)}
        >
          {activeCount}
        </span>
      </Show>

      <Popper {...popperProps} usePortal>
        <div
          className="max-h-[70vh] w-[38rem] overflow-y-auto rounded bg-palettes-white-10-100 p-3 shadow-lg"
          data-testid={suffixTestId('filters-panel', props)}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-primary">
              {i18n.t('page.datagrid.labels.filters')}
            </span>
            <Show when={activeCount > 0}>
              <button
                type="button"
                onClick={() => props.onFiltersChange(EMPTY_RENTAL_CALENDAR_FILTERS)}
                className="cursor-pointer bg-transparent text-xs font-bold text-link"
                data-testid={suffixTestId('filters-clearAll', props)}
              >
                {i18n.t('page.datagrid.labels.clearAll')}
              </button>
            </Show>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {/* Both pick out calendar rows directly, so they lead the panel */}
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('vehicleId')}
              value={props.filters.vehicleId}
              onChange={setFilter('vehicleId')}
              options={filterOptions.vehicleId}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-vehicle', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('carPark')}
              value={props.filters.carPark}
              onChange={setFilter('carPark')}
              options={filterOptions.carPark}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-carPark', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('vehicleCategory')}
              value={props.filters.vehicleCategory}
              onChange={setFilter('vehicleCategory')}
              options={filterOptions.vehicleCategory}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-category', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('vehicleType')}
              value={props.filters.vehicleType}
              onChange={setFilter('vehicleType')}
              options={filterOptions.vehicleType}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-vehicleType', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('make')}
              value={props.filters.make}
              onChange={setFilter('make')}
              options={filterOptions.make}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-make', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('modelFamily')}
              value={props.filters.modelFamily}
              onChange={setFilter('modelFamily')}
              options={filterOptions.modelFamily}
              isDisabled={props.filters.make.length === 0}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-model', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('bodyStyle')}
              value={props.filters.bodyStyle}
              onChange={setFilter('bodyStyle')}
              options={filterOptions.bodyStyle}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-body', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('fuelType')}
              value={props.filters.fuelType}
              onChange={setFilter('fuelType')}
              options={filterOptions.fuelType}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-fuel', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('transmission')}
              value={props.filters.transmission}
              onChange={setFilter('transmission')}
              options={filterOptions.transmission}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-transmission', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('drive')}
              value={props.filters.drive}
              onChange={setFilter('drive')}
              options={filterOptions.drive}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-drive', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('doorCount')}
              value={props.filters.doorCount}
              onChange={setFilter('doorCount')}
              options={filterOptions.doorCount}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-doorCount', props)}
            />
            <MultiChoice<string>
              label={getRentalCalendarFilterLabel('seatCount')}
              value={props.filters.seatCount}
              onChange={setFilter('seatCount')}
              options={filterOptions.seatCount}
              menuInPortal
              closeMenuOnSelect={false}
              data-testid={suffixTestId('filters-seatCount', props)}
            />
          </div>
        </div>
      </Popper>
    </div>
  );
}

/** The badge counts filters that narrow the result, not individual selected options */
const countActiveFilters = (filters: RentalCalendarFilterValues) =>
  values(filters).filter(isNonEmptyArray).length;

/**
 * Portaled react-select menu rendered by MultiChoice. Mirrors platform's own
 * `isReactSelectMenuClick`, which is not re-exported from `platform/components`.
 */
const CHOICE_MENU_SELECTOR =
  '[role="option"], [role="listbox"], [class*="eag--choice__menu"], [class*="eag--choice__option"]';
